import { createClient } from "@supabase/supabase-js";
import imageCompression from "browser-image-compression";
import slugify from "slugify";

// Create Supabase client for files storages
export const supabaseBucket = createClient(
  process.env.REACT_APP_SUPABASE_BUCKET_URL,
  process.env.REACT_APP_SUPABASE_BUCKET_ANON_KEY,
);

// Client for Auth only
export const supabaseAuth = createClient(
  process.env.REACT_APP_SUPABASE_AUTH_URL,
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_AUTH_DEFAULT_KEY,
);

// Generic image upload function
export const uploadImage = async (
  file: File,
  type: string = "spot",
  name: string = "",
  secondaryInfo: string = "",
): Promise<string | null> => {
  if (!file) return null;

  try {
    const compressionOptions = {
      maxSizeMB: 0.8,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: "image/jpeg" as const,
      initialQuality: 0.85,
    };

    // Compress the image
    const compressedFile = await imageCompression(file, compressionOptions);

    // Create filename based on type
    // Always use .jpg since we compress to JPEG format
    const extension = "jpg";
    const nameSlug = slugify(name, {
      lower: true,
      strict: true,
      replacement: "_",
    });
    const secondarySlug = slugify(secondaryInfo, {
      lower: true,
      strict: true,
      replacement: "_",
    });

    const filename =
      nameSlug && secondarySlug
        ? `${nameSlug}_${secondarySlug}.${extension}`
        : `${type}_${Date.now()}.${extension}`;

    const filePath = `public/${filename}`;

    // Determine bucket name based on type
    const bucketName =
      type === "workplace" ? "workplace-images" : "spot-images";

    // Upload file
    const { error: uploadError } = await supabaseBucket.storage
      .from(bucketName)
      .upload(filePath, compressedFile, {
        cacheControl: "3600",
        upsert: false,
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const {
      data: { publicUrl },
    } = supabaseBucket.storage.from(bucketName).getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`${type} image upload failed:`, message);
    return null;
  }
};
